import { z } from 'zod';
import {
  AbsenceApprovalSchema,
  AbsenceAttentionItemSchema,
  AbsenceRangeKindSchema,
  AbsenceTypeSchema,
} from './absence';
import { ReportPersonSchema, ReportScopeSchema } from './holidays';

/**
 * Phase 2b M3: the Bradford + trigger rules engine with acknowledgeable hits, the absence
 * settings editors (types and rules), the absence reports, and the shapes the web renders
 * for them (spec docs/specs/phase-2b-absence.md §3, §5 screens 1–3, §7 2b-M3; D44).
 *
 * The engine lives in apps/api/src/absence/rules.ts. Values are computed from the facts
 * (absences of types that count to Bradford, merged into spells with gaps of ≤ 1 working day)
 * over a rolling window per rule; a hit is one row per person + rule while the value sits at
 * or above the threshold, acknowledged with a note.
 */

const IsoDate = z.iso.date();
const Named = z.object({ id: z.uuid(), name: z.string() });
const Year = z.coerce.number().int().min(2000).max(2100);

// Rules -----------------------------------------------------------------------------------

export const AbsenceRuleMetricSchema = z.enum(['spells', 'days', 'bradford', 'lates_awol']);
export type AbsenceRuleMetric = z.infer<typeof AbsenceRuleMetricSchema>;

export const AbsenceRuleActionSchema = z.enum([
  'task_manager',
  'task_hr',
  'notify_manager',
  'notify_hr',
]);
export type AbsenceRuleAction = z.infer<typeof AbsenceRuleActionSchema>;

/** Who acts on a hit: the line manager (manager rules) or HR (HR rules). */
export const RuleAudienceSchema = z.enum(['manager', 'hr']);
export type RuleAudience = z.infer<typeof RuleAudienceSchema>;

export function audienceOfAction(action: AbsenceRuleAction): RuleAudience {
  return action === 'task_hr' || action === 'notify_hr' ? 'hr' : 'manager';
}

export const ABSENCE_RULE_METRIC_LABELS: Record<AbsenceRuleMetric, string> = {
  bradford: 'Bradford score (S² × D)',
  spells: 'Spells',
  days: 'Days',
  lates_awol: 'Lates + AWOL days',
};

export const ABSENCE_RULE_ACTION_LABELS: Record<AbsenceRuleAction, string> = {
  task_manager: 'Task for the line manager',
  task_hr: 'HR notified + task for HR',
  notify_manager: 'Notify the line manager',
  notify_hr: 'Notify HR',
};

/** Rolling window lengths: 3 → 90 days, 6 → 180, 12 → 365 (the old 90/180/365). */
export function windowDaysOf(months: number): number {
  return months % 12 === 0 ? (months / 12) * 365 : months * 30;
}

export const AbsenceRuleSchema = z.object({
  code: z.string(),
  name: z.string(),
  windowMonths: z.number().int(),
  /** windowDaysOf(windowMonths), for display. */
  windowDays: z.number().int(),
  metric: AbsenceRuleMetricSchema,
  threshold: z.number(),
  action: AbsenceRuleActionSchema,
  audience: RuleAudienceSchema,
  active: z.boolean(),
  /** Unacknowledged hits on the rule right now. */
  openHits: z.number().int(),
});
export type AbsenceRule = z.infer<typeof AbsenceRuleSchema>;

export const AbsenceRulesResponseSchema = z.object({ rules: z.array(AbsenceRuleSchema) });
export type AbsenceRulesResponse = z.infer<typeof AbsenceRulesResponseSchema>;

const RuleCode = z
  .string()
  .trim()
  .min(2)
  .max(32)
  .regex(/^[a-z][a-z0-9_]*$/, 'Lower-case letters, digits and underscores');

const RuleFields = z.object({
  name: z.string().trim().min(3).max(100),
  windowMonths: z.coerce.number().int().min(1).max(36),
  metric: AbsenceRuleMetricSchema,
  threshold: z.coerce.number().min(0.5).max(100000).multipleOf(0.1),
  action: AbsenceRuleActionSchema,
  active: z.boolean(),
});

/** POST /absence/rules */
export const AbsenceRuleCreateSchema = RuleFields.extend({
  code: RuleCode,
  active: z.boolean().optional(),
});
export type AbsenceRuleCreate = z.infer<typeof AbsenceRuleCreateSchema>;

/** PATCH /absence/rules/:code */
export const AbsenceRulePatchSchema = RuleFields.partial();
export type AbsenceRulePatch = z.infer<typeof AbsenceRulePatchSchema>;

export const AbsenceRuleResponseSchema = z.object({ rule: AbsenceRuleSchema });
export type AbsenceRuleResponse = z.infer<typeof AbsenceRuleResponseSchema>;

/** POST /absence/rules/preview: the edited rule, before saving. */
export const AbsenceRulePreviewSchema = z.object({
  /** The rule being edited (null/absent for a new one): reports today's count for comparison. */
  code: z.string().max(32).optional(),
  metric: AbsenceRuleMetricSchema,
  windowMonths: z.coerce.number().int().min(1).max(36),
  threshold: z.coerce.number().min(0.5).max(100000),
});
export type AbsenceRulePreview = z.infer<typeof AbsenceRulePreviewSchema>;

export const AbsenceRulePreviewResponseSchema = z.object({
  /** Active staff evaluated. */
  people: z.number().int(),
  /** How many would be at or above the edited threshold today. */
  count: z.number().int(),
  /** The same count for the saved rule (null for a new rule). */
  currentCount: z.number().int().nullable(),
  /** The highest values, for a feel of the spread. */
  top: z.array(z.object({ personId: z.uuid(), name: z.string(), value: z.number() })),
});
export type AbsenceRulePreviewResponse = z.infer<typeof AbsenceRulePreviewResponseSchema>;

// Hits ------------------------------------------------------------------------------------

export const RuleHitStatusSchema = z.enum(['open', 'acknowledged', 'closed']);
export type RuleHitStatus = z.infer<typeof RuleHitStatusSchema>;

export const RuleHitSchema = z.object({
  id: z.uuid(),
  personId: z.uuid(),
  ruleCode: z.string(),
  ruleName: z.string(),
  metric: AbsenceRuleMetricSchema,
  threshold: z.number(),
  action: AbsenceRuleActionSchema,
  audience: RuleAudienceSchema,
  /** The value when it fired (updated while it stays open). */
  value: z.number(),
  computedAt: z.iso.datetime(),
  status: RuleHitStatusSchema,
  acknowledgedBy: Named.nullable(),
  acknowledgedAt: z.iso.datetime().nullable(),
  note: z.string().nullable(),
  /** Closed without acknowledgement because the value fell below the threshold. */
  closedAt: z.iso.datetime().nullable(),
  closeNote: z.string().nullable(),
});
export type RuleHit = z.infer<typeof RuleHitSchema>;

export const BradfordBandSchema = z.object({
  ruleCode: z.string(),
  name: z.string(),
  threshold: z.number(),
  /** 0 = the lowest band; higher is worse. */
  level: z.number().int(),
});
export type BradfordBand = z.infer<typeof BradfordBandSchema>;

export const PersonRuleValueSchema = z.object({
  rule: AbsenceRuleSchema,
  value: z.number(),
  /** value ≥ threshold today. */
  over: z.boolean(),
  openHit: RuleHitSchema.nullable(),
});
export type PersonRuleValue = z.infer<typeof PersonRuleValueSchema>;

/** GET /absence/people/:id/rules */
export const PersonRulesResponseSchema = z.object({
  personId: z.uuid(),
  today: IsoDate,
  bradford: z.object({
    score: z.number(),
    spells: z.number().int(),
    days: z.number(),
    /** The highest active Bradford band reached, or null below the lowest. */
    band: BradfordBandSchema.nullable(),
    /** Every active band, lowest first (for the chip legend). */
    bands: z.array(BradfordBandSchema),
  }),
  /** Active rules, Bradford bands first, then by window and metric. */
  rules: z.array(PersonRuleValueSchema),
  /** Every hit on record, newest first (open, acknowledged, closed). */
  hits: z.array(RuleHitSchema),
  /** Which hits the caller may acknowledge. */
  canAcknowledge: z.object({ manager: z.boolean(), hr: z.boolean() }),
  /** Lateness arrives from T&A in Phase 3; until then lates_awol counts AWOL days only. */
  latenessAvailable: z.boolean(),
});
export type PersonRulesResponse = z.infer<typeof PersonRulesResponseSchema>;

/** POST /absence/hits/:id/acknowledge */
export const AcknowledgeHitSchema = z.object({ note: z.string().trim().min(3).max(500) });
export type AcknowledgeHit = z.infer<typeof AcknowledgeHitSchema>;

export const AcknowledgeHitResponseSchema = z.object({ hit: RuleHitSchema });
export type AcknowledgeHitResponse = z.infer<typeof AcknowledgeHitResponseSchema>;

/** GET /absence/hits?site=|region=&audience= : the open-hits queue (HR › Absence › Rule hits). */
export const RuleHitsQuerySchema = z.object({
  site: z.string().trim().max(32).optional(),
  region: z.string().trim().max(32).optional(),
  audience: RuleAudienceSchema.optional(),
});
export type RuleHitsQuery = z.infer<typeof RuleHitsQuerySchema>;

export const RuleHitQueueItemSchema = RuleHitSchema.extend({
  name: z.string(),
  initials: z.string(),
  jobTitle: z.string().nullable(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  /** The caller may acknowledge it. */
  canAcknowledge: z.boolean(),
});
export type RuleHitQueueItem = z.infer<typeof RuleHitQueueItemSchema>;

export const RuleHitsResponseSchema = z.object({
  today: IsoDate,
  items: z.array(RuleHitQueueItemSchema),
  /** Open hits per rule, in rule order (the "by band" header). */
  byRule: z.array(z.object({ ruleCode: z.string(), ruleName: z.string(), count: z.number() })),
});
export type RuleHitsResponse = z.infer<typeof RuleHitsResponseSchema>;

// Type settings ----------------------------------------------------------------------------

const TypeCode = z
  .string()
  .trim()
  .min(2)
  .max(32)
  .regex(/^[a-z][a-z0-9_]*$/, 'Lower-case letters, digits and underscores');

export const PAY_CATEGORIES = ['sick_pay', 'paid', 'unpaid', 'statutory', 'per_policy'] as const;

const TypeFlags = z.object({
  name: z.string().trim().min(2).max(100),
  countsToBradford: z.boolean(),
  needsApproval: AbsenceApprovalSchema,
  allowsPartDay: z.boolean(),
  requiresRtw: z.boolean(),
  fitNoteAfterDays: z.number().int().min(1).max(365).nullable(),
  visibleToStaff: z.boolean(),
  payCategory: z.string().trim().min(2).max(32),
  active: z.boolean(),
  sort: z.number().int().min(0).max(9999),
});

/** POST /absence/types (absence.admin.manage): a new type; types are never deleted. */
export const AbsenceTypeCreateSchema = TypeFlags.extend({
  code: TypeCode,
  rangeKind: AbsenceRangeKindSchema,
  active: z.boolean().optional(),
  sort: z.number().int().min(0).max(9999).optional(),
});
export type AbsenceTypeCreate = z.infer<typeof AbsenceTypeCreateSchema>;

/** PATCH /absence/types/:code: the HR-editable flags (deactivate rather than delete). */
export const AbsenceTypePatchSchema = TypeFlags.partial();
export type AbsenceTypePatch = z.infer<typeof AbsenceTypePatchSchema>;

export const AbsenceTypeResponseSchema = z.object({
  type: AbsenceTypeSchema,
  /** Absences on record of the type (any status), so HR sees what a change touches. */
  inUse: z.number().int(),
});
export type AbsenceTypeResponse = z.infer<typeof AbsenceTypeResponseSchema>;

// Reports --------------------------------------------------------------------------------

/**
 * Every absence report takes an optional site or region inside the caller's
 * absence.reports.view scope, and a period: `from`/`to`, or a `year` (default: this year to
 * date). by-site works by calendar month over the year of `from`.
 */
export const AbsenceReportQuerySchema = z.object({
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  year: Year.optional(),
  site: z.string().trim().max(32).optional(),
  region: z.string().trim().max(32).optional(),
  format: z.enum(['json', 'csv']).optional(),
});
export type AbsenceReportQuery = z.infer<typeof AbsenceReportQuerySchema>;

const TypeRef = z.object({ code: z.string(), name: z.string() });

export const AbsenceSummaryRowSchema = ReportPersonSchema.extend({
  spells: z.number().int(),
  days: z.number(),
  /** Days in the period per type code. */
  byType: z.record(z.string(), z.number()),
  /** Rolling 365 days to today. */
  bradford: z.number(),
  band: z.string().nullable(),
});
export type AbsenceSummaryRow = z.infer<typeof AbsenceSummaryRowSchema>;

export const AbsenceSummaryReportSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  today: IsoDate,
  scope: ReportScopeSchema,
  /** The types that appear in `byType`, catalogue order. */
  types: z.array(TypeRef),
  items: z.array(AbsenceSummaryRowSchema),
  totals: z.object({
    people: z.number().int(),
    absentPeople: z.number().int(),
    spells: z.number().int(),
    days: z.number(),
    byType: z.record(z.string(), z.number()),
  }),
});
export type AbsenceSummaryReport = z.infer<typeof AbsenceSummaryReportSchema>;

export const AbsenceBySiteReportSchema = z.object({
  year: z.number().int(),
  today: IsoDate,
  scope: ReportScopeSchema,
  sites: z.array(z.object({ code: z.string(), name: z.string() })),
  /** Twelve rows, January first: working days lost in the month, by site and by group. */
  months: z.array(
    z.object({
      month: z.string(),
      label: z.string(),
      total: z.number(),
      sickness: z.number(),
      awol: z.number(),
      other: z.number(),
      bySite: z.record(z.string(), z.number()),
    }),
  ),
  total: z.number(),
});
export type AbsenceBySiteReport = z.infer<typeof AbsenceBySiteReportSchema>;

export const BradfordReportRowSchema = ReportPersonSchema.extend({
  score: z.number(),
  spells: z.number().int(),
  days: z.number(),
});
export type BradfordReportRow = z.infer<typeof BradfordReportRowSchema>;

export const AbsenceBradfordReportSchema = z.object({
  today: IsoDate,
  scope: ReportScopeSchema,
  /** Highest band first; the last entry (`ruleCode` null) is everyone with a score below the lowest band. */
  bands: z.array(
    z.object({
      ruleCode: z.string().nullable(),
      name: z.string(),
      threshold: z.number().nullable(),
      people: z.array(BradfordReportRowSchema),
    }),
  ),
  /** People in scope with a score of 0 (not listed). */
  zero: z.number().int(),
});
export type AbsenceBradfordReport = z.infer<typeof AbsenceBradfordReportSchema>;

export const OpenSpellRowSchema = ReportPersonSchema.extend({
  absenceId: z.uuid(),
  type: TypeRef,
  startDate: IsoDate,
  daysSoFar: z.number(),
  expectedReturnOn: IsoDate.nullable(),
  certified: z.string(),
});
export type OpenSpellRow = z.infer<typeof OpenSpellRowSchema>;

export const AbsenceOpenReportSchema = z.object({
  today: IsoDate,
  scope: ReportScopeSchema,
  open: z.array(OpenSpellRowSchema),
  rtwOverdue: z.array(AbsenceAttentionItemSchema),
  fitNotesDue: z.array(AbsenceAttentionItemSchema),
});
export type AbsenceOpenReport = z.infer<typeof AbsenceOpenReportSchema>;

export const AbsenceTypesReportSchema = z.object({
  from: IsoDate,
  to: IsoDate,
  scope: ReportScopeSchema,
  items: z.array(
    TypeRef.extend({
      spells: z.number().int(),
      people: z.number().int(),
      days: z.number(),
      countsToBradford: z.boolean(),
    }),
  ),
  total: z.number(),
});
export type AbsenceTypesReport = z.infer<typeof AbsenceTypesReportSchema>;
